import { PercentPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ScheduleData, ScheduleName, SchedulePayload } from '@approot/models';
import {
  ConfigService,
  LegService,
  MARKET_DATA_PAST_DATE,
  PricingExtraService,
  ProductsConfigService,
  StrategyDetailService
} from '@approot/services';
import { DateUtilities, Utilities } from '@approot/utilities';
import {
  completeScheduleData,
  firstDateToUseFn,
  frequencyToUseFn,
  lastDateToUseFn,
  numberOfXToUseFn
} from '@approot/utilities/schedule-utilities';
import {
  clearTextColor,
  markFieldAsPending,
  removePendingFromField,
  setButtonText
} from '../utilities/leg.utilities';
import { LegHelperService } from './leg-helper.service';

interface RateIndexDates {
  startDate: string;
  tenor:
    | 'firstExerciseDate'
    | 'swapStartDate'
    | 'swaptionExpiryDate'
    | 'swapEndDate'
    | 'swaptionSettlementDate';
}

interface RateIndexValueDates {
  setDate1?: string;
  setDate2?: string;
  dateToUse?: string;
}
@Injectable({
  providedIn: 'root'
})
export class LegScheduleService {
  constructor(
    private legHelperService: LegHelperService,
    private percentPipe: PercentPipe,
    private legService: LegService,
    private productsConfigService: ProductsConfigService,
    private strategyDetailService: StrategyDetailService,
    private pricingExtraService: PricingExtraService,
    private configService: ConfigService
  ) {}

  public callSwapScheduleGenerator(scheduleName: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    if (
      this.legHelperService.presetsLoaded &&
      form.get(scheduleName) &&
      form.get('currency') &&
      form.get('swapStartDate')?.valid &&
      form.get('swapEndDate')?.valid &&
      ((scheduleName === 'fixedLegSchedule' &&
        form.get('fixedLegFrequency')?.valid &&
        form.get('fixedLegConvention')?.valid) ||
        (scheduleName === 'floatingLegSchedule' &&
          form.get('floatingLegIndex')?.valid &&
          form.get('floatingLegConvention')?.valid &&
          form.get('floatingLegFrequency')?.valid))
    ) {
      const payload = {
        task: 'legSchedule',
        currency: form.get('currency').value,
        startDate: DateUtilities.prettyDateToDate(
          form.get('swapStartDate').value
        ),
        endDate: DateUtilities.prettyDateToDate(form.get('swapEndDate').value),
        frequency:
          scheduleName === 'fixedLegSchedule'
            ? form.get('fixedLegFrequency').value
            : form.get('floatingLegFrequency').value,
        dayCountConvention:
          scheduleName === 'fixedLegSchedule'
            ? form.get('fixedLegConvention').value
            : form.get('floatingLegConvention').value
      };

      if (scheduleName === 'floatingLegSchedule') {
        payload['index'] = form.get('floatingLegIndex').value;
      }

      this.legService.dateService(payload).subscribe((response: any) => {
        completeScheduleData(
          response,
          scheduleName,
          form,
          leg,
          this.legHelperService.rfqExecutionSettings.rfqLeg,
          this.percentPipe,
          this.configService.userSettings()
        );
        this.setScheduleValidators(scheduleName);
        setButtonText(scheduleName, 'Periods', leg, form);
      });
    }
  }

  public callScheduleGenerator(): void {
    if (!this.legHelperService.presetsLoaded) {
      return;
    }
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const firstDateToUse = firstDateToUseFn(form);
    const lastDateToUse = lastDateToUseFn(form);
    const numberOfXToUse = numberOfXToUseFn(form);
    const frequencyToUse = frequencyToUseFn(form);
    const fixingHolidayCalendarsCtrl = form.get('fixingHolidayCalendars');
    const cross = form.get('cross')?.value;
    if (
      form.get(firstDateToUse)?.valid &&
      form.get(frequencyToUse)?.value &&
      form.get(numberOfXToUse)?.valid &&
      (!fixingHolidayCalendarsCtrl || fixingHolidayCalendarsCtrl.valid) &&
      cross
    ) {
      let formattedDate = '';
      if (DateUtilities.isPrettyDate(form.get(firstDateToUse).value)) {
        formattedDate = DateUtilities.prettyDateToSlashedDate(
          form.get(firstDateToUse).value
        );
      } else {
        formattedDate = form.get(firstDateToUse).value;
      }

      if (lastDateToUse) {
        markFieldAsPending(form, leg, lastDateToUse);
      }

      const fixingSource = this.productsConfigService.getFixingSourceCode(
        form.get('fixingSource')?.value
      );
      const cutOff = this.productsConfigService.getCutOffCode(
        form.get('cutOff')?.value
      );
      const firstDate = DateUtilities.restructureDate(formattedDate);
      const currencyPair = cross.replace('/', '');
      const frequency = form.get(frequencyToUse).value;
      const numberOfXToUseValue = form.get(numberOfXToUse).value;
      let payload: SchedulePayload = {
        frequency,
        businessDayConvention: 'Following',
        currencyPair: currencyPair,
        referenceDate:
          this.strategyDetailService.marketDataToUse === MARKET_DATA_PAST_DATE
            ? this.pricingExtraService.getMarketDataTimeStamp
            : DateUtilities.getDateInISOFormat(),
        [firstDateToUse]: firstDate,
        [numberOfXToUse]: numberOfXToUseValue,
        fixingSource,
        cutOff,
        settlementFrequency: form.get('settlementFrequency')?.value
      };

      if (fixingHolidayCalendarsCtrl) {
        payload.fixingHolidayCalendars = fixingHolidayCalendarsCtrl.value || [];
      }

      this.legService.calculateSchedule(payload).subscribe({
        next: (scheduleResults: ScheduleData[]) => {
          this.handleCalculateScheduleResp(scheduleResults, lastDateToUse);
        },
        error: _ =>
          removePendingFromField(form, leg, rfqExecutionSettings, lastDateToUse)
      });
    }
  }

  public exerciseScheduleGenerator(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const firstExerciseDate = form.get('firstExerciseDate').value;
    if (
      form.get('firstExerciseDate')?.valid &&
      form.get('exerciseFrequency')?.valid &&
      form.get('nbExerciseDates')?.valid &&
      DateUtilities.isPrettyDate(firstExerciseDate)
    ) {
      const payload = {
        task: 'excerciseDateSchedule',
        frequency: form.get('exerciseFrequency').value,
        firstDate: DateUtilities.prettyDateToDate(firstExerciseDate),
        nDates: form.get('nbExerciseDates').value
      };

      this.legService
        .dateService(payload)
        .subscribe(
          (response: { exerciseSchedule: { exerciseDate: string }[] }) => {
            completeScheduleData(
              response.exerciseSchedule,
              'exerciseSchedule',
              form,
              leg,
              this.legHelperService.rfqExecutionSettings.rfqLeg,
              this.percentPipe,
              this.configService.userSettings()
            );
            this.setScheduleValidators('exerciseSchedule');
            setButtonText('exerciseSchedule', 'Exercises', leg, form);
          }
        );
    }
  }

  public rateIndexDateAdd(dates: RateIndexDates): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    if (
      this.legHelperService.presetsLoaded &&
      form.get('floatingLegIndex')?.valid &&
      (!dates.startDate || form.get(dates.startDate)?.valid) &&
      (!dates.tenor ||
        DateUtilities.isCorrectFullDateFormat(form.get(dates.tenor).value) ||
        DateUtilities.isValidLag(form.get(dates.tenor).value))
    ) {
      const payload = {
        task: 'rateIndexDateAdd',
        index: form.get('floatingLegIndex').value
      };

      if (dates.tenor) {
        payload['tenor'] = DateUtilities.getDateInDashedFormat(
          form.get(dates.tenor).value
        );
      }

      if (dates.startDate) {
        payload['date'] = DateUtilities.getDateInDashedFormat(
          form.get(dates.startDate).value
        );
      } else {
        payload['date'] = this.pricingExtraService.getMarketDataTimeStamp
          ? this.pricingExtraService.getMarketDataTimeStamp
          : DateUtilities.getDateInISOFormat();
      }

      markFieldAsPending(form, leg, dates.tenor);
      this.legService.dateService(payload).subscribe(
        (response: any) => {
          this.handleRateIndexDateAddResp(response, dates);
        },
        err => {
          removePendingFromField(form, leg, rfqExecutionSettings, dates.tenor);
        }
      );
    }
  }

  public rateIndexValueDateFromSetDate(dates: RateIndexValueDates): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    if (
      this.legHelperService.presetsLoaded &&
      form.get('floatingLegIndex').valid &&
      (!dates.setDate1 || form.get(dates.setDate1)) &&
      (!dates.setDate2 || form.get(dates.setDate2)) &&
      (!dates.dateToUse ||
        (form.get(dates.dateToUse) && form.get(dates.dateToUse).valid))
    ) {
      const payload = {
        task: 'rateIndexValueDateFromSetDate',
        index: form.get('floatingLegIndex').value
      };

      if (dates.dateToUse) {
        payload['date'] = DateUtilities.getDateInDashedFormat(
          form.get(dates.dateToUse).value
        );
      } else {
        payload['date'] = this.pricingExtraService.getMarketDataTimeStamp
          ? this.pricingExtraService.getMarketDataTimeStamp
          : DateUtilities.getDateInISOFormat();
      }

      markFieldAsPending(form, leg, dates.setDate1);
      markFieldAsPending(form, leg, dates.setDate2);

      this.legService.dateService(payload).subscribe(
        (response: any) => {
          this.rateIndexValueDateResp(response, dates);
        },
        err => {
          removePendingFromField(
            form,
            leg,
            rfqExecutionSettings,
            dates.setDate1
          );
          removePendingFromField(
            form,
            leg,
            rfqExecutionSettings,
            dates.setDate2
          );
        }
      );
    }
  }

  public updateScheduleValidity(scheduleName: ScheduleName): void {
    const scheduleCtrl = this.legHelperService.form.get(
      scheduleName
    ) as FormArray;
    scheduleCtrl?.updateValueAndValidity();
    scheduleCtrl?.controls.forEach((scheduleRow: FormGroup) =>
      Object.keys(scheduleRow.controls).forEach(shortName =>
        scheduleRow.get(shortName).updateValueAndValidity()
      )
    );
  }

  public xccySwapGetSchedules(fieldChanged: Record<string, any>): void {
    const form = this.legHelperService.form;
    if (
      this.legHelperService.presetsLoaded &&
      form.get('fixedLegSchedule1') &&
      form.get('fixedLegSchedule2') &&
      form.get('cross').valid &&
      form.get('xccySwapEndDate').valid &&
      form.get('xccySwapStartDate').valid &&
      form.get('frequency1').valid &&
      form.get('frequency2').valid &&
      form.get('xccySwapFixedLegConvention1').valid &&
      form.get('xccySwapFixedLegConvention2').valid
    ) {
      const product = this.productsConfigService.productFormatter(
        { ...this.legHelperService.form.value, ...fieldChanged },
        this.legHelperService.leg.attributes
      );
      this.legService
        .dateService({
          task: 'xccySwapGetSchedules',
          product
        })
        .subscribe(
          (resp: {
            fixedLegSchedule1: ScheduleData[];
            fixedLegSchedule2: ScheduleData[];
          }) => {
            Object.keys(resp).forEach(
              (key: 'fixedLegSchedule1' | 'fixedLegSchedule2') => {
                [
                  'notional1',
                  'notional2',
                  'fixedLegRate1',
                  'fixedLegRate2'
                ].forEach(field => {
                  if (form.get(field).value === Utilities.customText) {
                    form.get(field).setValue('');
                  }
                });

                completeScheduleData(
                  resp[key],
                  key,
                  form,
                  this.legHelperService.leg,
                  this.legHelperService.rfqExecutionSettings.rfqLeg,
                  this.percentPipe,
                  this.configService.userSettings(),
                  key === 'fixedLegSchedule1' ? '1' : '2'
                );
                this.setScheduleValidators(key);
                setButtonText(key, 'Periods', this.legHelperService.leg, form);
              }
            );
          }
        );
    }
  }

  private rateIndexValueDateResp(
    response: any,
    dates: RateIndexValueDates
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    removePendingFromField(form, leg, rfqExecutionSettings, dates.setDate1);
    removePendingFromField(form, leg, rfqExecutionSettings, dates.setDate2);

    if (dates.setDate1) {
      form
        .get(dates.setDate1)
        .setValue(DateUtilities.dateToPrettyDate(response.date), {
          emitEvent: false
        });
    }

    if (dates.setDate2) {
      form
        .get(dates.setDate2)
        .setValue(DateUtilities.dateToPrettyDate(response.date), {
          emitEvent: false
        });
    }

    if (dates.dateToUse === 'swaptionExpiryDate') {
      this.callSwapScheduleGenerator('fixedLegSchedule');
      this.callSwapScheduleGenerator('floatingLegSchedule');
    } else {
      this.rateIndexDateAdd({
        startDate: 'swapStartDate',
        tenor: 'swapEndDate'
      });
    }
  }

  private handleRateIndexDateAddResp(
    response: any,
    dates: RateIndexDates
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    form
      .get(dates.tenor)
      .setValue(DateUtilities.dateToPrettyDate(response.date), {
        emitEvent: false
      });
    removePendingFromField(form, leg, rfqExecutionSettings, dates.tenor);

    if (dates.tenor !== 'firstExerciseDate') {
      if (dates.tenor === 'swaptionExpiryDate') {
        this.rateIndexValueDateFromSetDate({
          setDate1: 'swapStartDate',
          setDate2: 'swaptionSettlementDate',
          dateToUse: 'swaptionExpiryDate'
        });
      } else if (dates.tenor === 'swapStartDate') {
        this.rateIndexDateAdd({
          startDate: 'swapStartDate',
          tenor: 'swapEndDate'
        });
      } else if (dates.tenor === 'swapEndDate') {
        form.get(dates.tenor).updateValueAndValidity({ emitEvent: false }); // update validity but we don't want the valuechanges to be called again

        if (!form.get(dates.tenor).valid) {
          // we want clarity design to display an invalid state
          form
            .get(dates.tenor)
            .setErrors({ error: 'error with ' + dates.tenor });
        }
        this.callSwapScheduleGenerator('fixedLegSchedule');
        this.callSwapScheduleGenerator('floatingLegSchedule');
      }
    } else {
      this.exerciseScheduleGenerator();
    }
  }

  private handleCalculateScheduleResp(
    scheduleResults: ScheduleData[],
    lastDateToUse: string
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    removePendingFromField(form, leg, rfqExecutionSettings, lastDateToUse);
    this.legHelperService.requiredFixings = [];

    completeScheduleData(
      scheduleResults,
      'schedule',
      form,
      leg,
      this.legHelperService.rfqExecutionSettings.rfqLeg,
      this.percentPipe,
      this.configService.userSettings()
    );
    this.setScheduleValidators('schedule');
    this.updateScheduleValidity('schedule');
    // for any schedule dates in the past, we need to get fixings
    let dateType: string;
    if (lastDateToUse === 'lastSettlementDate') {
      dateType = 'settlementDate';
    } else if (lastDateToUse === 'lastFixingDate') {
      dateType = 'fixingDate';
    } else if (lastDateToUse === 'lastExpirationDate') {
      dateType = 'expiryDate';
    }

    const prettyDate = DateUtilities.dateToPrettyDate(
      scheduleResults[scheduleResults.length - 1][dateType]
    );

    clearTextColor(leg, lastDateToUse); // wipe the custom color if its been set

    form.get(lastDateToUse).patchValue(prettyDate, { emitEvent: false });
    this.legHelperService.onChangeInLegFn();
    setButtonText('schedule', 'Fixings', leg, form);
  }

  public setScheduleValidators(
    scheduleName: ScheduleName,
    schedule?: FormArray
  ): void {
    const form = this.legHelperService.form;
    const scheduleCtrl = schedule || (form.get(scheduleName) as FormArray);
    if (
      !scheduleCtrl ||
      !(
        scheduleName === 'schedule' &&
        this.legHelperService.rfqExecutionSettings.rfqLeg
      )
    ) {
      return;
    }
    scheduleCtrl.setValidators([]);
    scheduleCtrl.addValidators(koBarrierAKO.bind(scheduleCtrl, form));
  }
}

function koBarrierAKO(
  form: FormGroup,
  scheduleFormArray: FormArray
): { invalidEntry: { value: string } } | null {
  const scheduleValue = scheduleFormArray.value as Record<string, any>[];
  const koBarrierTypeCtrl = form.get('koBarrierType');
  if (koBarrierTypeCtrl?.value === 'AKO') {
    const koBarriersSame = scheduleValue.every(
      (value, _, arr) => +arr[0].koBarrier === +value.koBarrier
    );
    if (!koBarriersSame) {
      return {
        invalidEntry: {
          value:
            'When the knock out barrier type is AKO, all knock out barriers must be the same'
        }
      };
    }
  }
  return null;
}
